/**
 * Work: named jobs in progress, tasks, and a searchable directory to start anything. A job keeps
 * its animals and results together; each record still lives on its animal.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ROLE_LABELS, type JobDetail, type JobItemDTO, type JobSummary, type TaskDTO, type WorkspaceGroup } from "../../../contract";
import { api } from "../../lib/api";
import { formatDate, relativeDay } from "../../lib/format";
import { plural, t, useLanguage } from "../../lib/i18n";
import { invalidateAll, useApi } from "../../lib/query";
import { useCan, useMe, useRanchToday } from "../../lib/session";
import { RefPicker, useCrew } from "../../forms/pickers";
import { openRecord } from "../../forms/EventForm";
import { jobComponent, registry, workspaceComponent } from "../../registry";
import { Button, Card, Checkbox, Chip, Empty, ErrorNote, Field, Icon, List, Loading, Menu, PageHeader, Progress, Row, Section, Select, Sheet, Tabs, TextInput, cx, toast, useFromHere } from "../../ui/kit";
import { useCaptureSubject, useOpenComposer } from "../Composer";
import { Page, usePins } from "../Layout";
import { fold } from "../tools";
import { NotFound } from "./NotFound";
import { Timeline } from "../Timeline";

const GROUPS: Array<{ id: WorkspaceGroup; label: string }> = [
  { id: "breed", label: "Breed and develop" }, { id: "care", label: "Care and measure" }, { id: "operations", label: "Ranch operations" }, { id: "sell", label: "Sell and exchange" }, { id: "data", label: "Records and data" },
];

export function TaskRow({ task, today }: { task: TaskDTO; today: string }) {
  const [busy, setBusy] = useState(false);
  const overdue = task.status === "open" && task.due_at && task.due_at.slice(0, 10) < today;
  const title = task.title_msg ? t(task.title_msg.text, task.title_msg.values) : task.title;
  const done = async (v: boolean) => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}`, { status: v ? "done" : "open" });
      await invalidateAll();
      if (v) toast(t("Done"), { tone: "good", action: { label: t("Undo"), run: () => void api.patch(`/tasks/${task.id}`, { status: "open" }).then(invalidateAll) } });
    } finally { setBusy(false); }
  };
  return (
    <li className={cx("task-row", task.status === "done" && "is-done")}>
      <Checkbox checked={task.status === "done"} onChange={(v) => void done(v)} label={t("Mark done: {{task}}", { task: title })} className={busy ? "busy" : undefined} />
      <div className="task-main">
        <span className="task-title">{title}</span>
        <span className="task-meta">
          {task.due_at && <span className={overdue ? "is-overdue" : undefined}><Icon name="calendar" size={13} />{relativeDay(task.due_at, today)}</span>}
          {task.subject && <Link to={task.subject.kind === "animal" ? `/cattle/${task.subject.id}` : task.subject.kind === "place" ? `/ranch/places/${task.subject.id}` : "#"}>{task.subject.label}</Link>}
          {task.assignee && <span><Icon name="people" size={13} />{task.assignee.label}</span>}
          {task.repeat_rule && <span><Icon name="reset" size={13} />{t(task.repeat_rule === "weekly" ? "Weekly" : task.repeat_rule === "monthly" ? "Monthly" : task.repeat_rule === "yearly" ? "Yearly" : "Daily")}</span>}
          {task.job && <Link to={`/work/jobs/${task.job.id}`}>{task.job.label}</Link>}
        </span>
      </div>
    </li>
  );
}

export function WorkHome() {
  useLanguage();
  const [params] = useSearchParams();
  const jobs = useApi<{ items: JobSummary[] }>("/jobs");
  const tasks = useApi<{ items: TaskDTO[] }>("/tasks?status=open");
  const { data: me } = useMe();
  const [mine, setMine] = useState(false);
  const [starting, setStarting] = useState(params.get("start") === "1");
  const today = useRanchToday();
  const shownTasks = (tasks.data?.items ?? []).filter((x) => !mine || x.assignee?.id === me?.membership?.id);
  const { pins } = usePins();
  return (
    <Page>
      <PageHeader title={t("Work")} actions={<Button variant="primary" icon="plus" onClick={() => setStarting(true)}>{t("Start something")}</Button>} />
      {pins.length > 0 && (
        <nav className="pin-chips" aria-label={t("Pinned")}>
          {pins.map((p) => <Link key={p.id} to={p.to} className="pin-chip"><Icon name={p.icon} size={16} />{t(p.label)}</Link>)}
        </nav>
      )}
      <Section title={t("In progress")} count={jobs.data?.items.length}>
        {jobs.isLoading ? <Loading /> : !jobs.data?.items.length ? <p className="muted teach">{t("No jobs running. Start one below, or tell Hondo: “Start preg checks on the spring calvers.”")}</p> : (
          <div className="cards">
            {jobs.data.items.map((j) => {
              const def = registry.jobs.get(j.type);
              return (
                <Card key={j.id} to={`/work/jobs/${j.id}`} className="job-card">
                  <span className="job-kind"><Icon name={def?.icon ?? "work"} size={14} />{def ? t(def.label) : j.type}</span>
                  <span className="job-title">{j.title}</span>
                  {j.progress.total > 0 && <Progress done={j.progress.done} total={j.progress.total} exceptions={j.progress.exceptions} />}
                  <span className="job-due">{j.due_at ? <><Icon name="calendar" size={14} />{relativeDay(j.due_at, today)}</> : j.owner?.label}</span>
                </Card>
              );
            })}
          </div>
        )}
      </Section>
      <Section title={t("Tasks")} count={shownTasks.length} action={<div className="row-actions"><Button size="sm" variant="ghost" onClick={() => setMine(!mine)}>{mine ? t("Everyone's") : t("Just mine")}</Button><Button size="sm" variant="ghost" to="/work/tasks">{t("All tasks")}</Button></div>}>
        {shownTasks.length === 0 ? <p className="muted">{t("No open tasks.")}</p> : <List>{shownTasks.slice(0, 12).map((x) => <TaskRow key={x.id} task={x} today={today} />)}</List>}
        <QuickTask />
      </Section>
      <Directory />
      <StartJobSheet open={starting} onClose={() => setStarting(false)} animals={params.get("animals")?.split(",") ?? []} />
    </Page>
  );
}

function QuickTask() {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const can = useCan();
  if (!can("record")) return null;
  return (
    <form className="quick-task" onSubmit={async (e) => {
      e.preventDefault();
      if (!title.trim()) return;
      setBusy(true);
      try { await api.post("/tasks", { title }); setTitle(""); await invalidateAll(); } finally { setBusy(false); }
    }}>
      <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Add a task: fix the gate in South")} aria-label={t("New task")} />
      <Button type="submit" icon="plus" busy={busy} disabled={!title.trim()}>{t("Add")}</Button>
    </form>
  );
}

/** Pin or unpin a workspace for this person, with Undo. */
export function useTogglePin() {
  const { ids, save } = usePins();
  return async (id: string) => {
    const before = ids;
    const pinned = ids.includes(id);
    const label = t(registry.workspaces.find((w) => w.id === id)?.label ?? id);
    await save(pinned ? ids.filter((p) => p !== id) : [...ids, id]);
    toast(pinned ? t("Unpinned {{name}}", { name: label }) : t("Pinned {{name}}", { name: label }), { action: { label: t("Undo"), run: () => void save(before) } });
  };
}

/** A pin toggle for a workspace page header: `<PinButton workspace="calving" />`. */
export function PinButton({ workspace }: { workspace: string }) {
  const { ids } = usePins();
  const toggle = useTogglePin();
  const pinned = ids.includes(workspace);
  const label = t(registry.workspaces.find((w) => w.id === workspace)?.label ?? workspace);
  return <Button variant="ghost" icon={pinned ? "pin" : "unpin"} aria-pressed={pinned} onClick={() => void toggle(workspace)} title={pinned ? t("Unpin {{name}}", { name: label }) : t("Pin {{name}}", { name: label })}>{pinned ? t("Pinned") : t("Pin")}</Button>;
}

/** Every workspace, grouped by the kind of work, searchable in both languages. */
export function Directory() {
  const [q, setQ] = useState("");
  const can = useCan();
  const { ids: pins } = usePins();
  const togglePin = useTogglePin();
  const lang = useLanguage();
  const matches = useMemo(() => {
    const words = fold(q).split(/\s+/).filter(Boolean);
    return registry.workspaces.filter((w) => (!w.permission || can(w.permission)) && words.every((word) => [t(w.label), t(w.description), ...w.keywords, ...w.keywords.map((k) => t(k)), ...(w.keywordsEs ?? [])].some((s) => fold(s).includes(word))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, lang]);
  if (!registry.workspaces.length) return null;
  return (
    <Section title={<span id="directory">{t("Start or open")}</span>} hint={t("Everything Hondo can do, by the kind of work. Pin the ones you use.")}>
      <div className="search-input"><Icon name="search" size={18} /><TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Register calves, preg check, sale catalog…")} aria-label={t("Find a workspace")} /></div>
      {GROUPS.map((g) => {
        const items = matches.filter((w) => w.group === g.id);
        if (!items.length) return null;
        return (
          <div key={g.id} className="directory-group">
            <h3>{t(g.label)}</h3>
            <div className="directory">
              {items.map((w) => (
                <div key={w.id} className="directory-item">
                  <Link to={`/work/${w.id}`} className="directory-link"><span className="row-icon"><Icon name={w.icon} size={18} /></span><span><strong>{t(w.label)}</strong><span className="muted">{t(w.description)}</span></span></Link>
                  <button type="button" className={cx("pin-btn", pins.includes(w.id) && "is-pinned")} aria-pressed={pins.includes(w.id)} aria-label={pins.includes(w.id) ? t("Unpin {{name}}", { name: t(w.label) }) : t("Pin {{name}}", { name: t(w.label) })} onClick={() => void togglePin(w.id)}><Icon name={pins.includes(w.id) ? "pin" : "unpin"} size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {matches.length === 0 && <p className="muted">{t("Nothing matches. Try telling Hondo what you want to do.")}</p>}
    </Section>
  );
}

function StartJobSheet({ open, onClose, animals }: { open: boolean; onClose: () => void; animals: string[] }) {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [group, setGroup] = useState<string | null>(null);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const jobs = [...registry.jobs.values()];
  const start = async () => {
    setBusy(true);
    try {
      let subjects = animals.map((id) => ({ kind: "animal", id }));
      if (group) {
        const g = await api.get<{ members: Array<{ id: string }> }>(`/groups/${group}`);
        subjects = [...subjects, ...g.members.map((m) => ({ kind: "animal", id: m.id }))];
      }
      const { id } = await api.post<{ id: string }>("/jobs", { type, title: title || t(registry.jobs.get(type)!.label), group_id: group, due_at: due || null, subjects });
      await invalidateAll();
      onClose();
      navigate(`/work/jobs/${id}`);
    } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("Start something")}>
      {jobs.length === 0 ? <p className="muted">{t("No job types yet.")}</p> : (
        <form onSubmit={(e) => { e.preventDefault(); void start(); }}>
          <Field label={t("What kind of work")}><Select value={type} onChange={(e) => setType(e.target.value)} required><option value="">{t("Choose…")}</option>{jobs.map((j) => <option key={j.type} value={j.type}>{t(j.label)}</option>)}</Select></Field>
          {type && <p className="field-hint">{t(registry.jobs.get(type)!.description)}</p>}
          <Field label={t("Name")} optional><TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={type ? t(registry.jobs.get(type)!.label) : ""} /></Field>
          <Field label={t("Which cattle")} hint={animals.length ? plural(animals.length, "{{count}} animal selected", "{{count}} animals selected") : t("Pick a group, or add animals later.")} optional><RefPicker target="group" value={group} onChange={(v) => setGroup(v as string | null)} /></Field>
          <Field label={t("Due")} optional><TextInput type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
          <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!type}>{t("Start")}</Button></div>
        </form>
      )}
    </Sheet>
  );
}

export function TasksPage() {
  const [status, setStatus] = useState("open");
  const tasks = useApi<{ items: TaskDTO[] }>(`/tasks?status=${status}`);
  const today = useRanchToday();
  return (
    <Page>
      <PageHeader back={{ to: "/work", label: t("Work") }} title={t("Tasks")} />
      <Tabs value={status} onChange={setStatus} tabs={[{ id: "open", label: t("Open") }, { id: "done", label: t("Done") }, { id: "all", label: t("All") }]} />
      <QuickTask />
      {tasks.isLoading ? <Loading /> : !tasks.data?.items.length ? <Empty icon="task" title={t("No tasks")} /> : <List>{tasks.data.items.map((x) => <TaskRow key={x.id} task={x} today={today} />)}</List>}
    </Page>
  );
}

/** A job: its animals as a worklist, the records made in it, and its tasks. Modules can replace the body. */
export function JobPage() {
  const { id } = useParams();
  const q = useApi<{ job: JobDetail; items: JobItemDTO[]; tasks: TaskDTO[] }>(`/jobs/${id}`);
  useCaptureSubject(q.data ? { job_id: q.data.job.id, subject: { kind: "job", id: q.data.job.id, label: q.data.job.title } } : null);
  const [tab, setTab] = useState("work");
  const today = useRanchToday();
  if (q.isLoading) return <Loading />;
  if (!q.data) return (q.error as { status?: number } | null)?.status === 404 ? <NotFound title={t("We couldn't find that job")} /> : <Page><ErrorNote error={q.error} retry={() => q.refetch()} /></Page>;
  const { job, items, tasks } = q.data;
  const def = registry.jobs.get(job.type);
  const Custom = jobComponent(job.type);
  const ws = registry.workspaces.find((w) => w.id === job.workspace);
  const setStatus = async (status: string) => { await api.patch(`/jobs/${job.id}`, { status }); await q.refetch(); await invalidateAll(); };
  return (
    <Page>
      <PageHeader back={ws ? { to: `/work/${ws.id}`, label: t(ws.label) } : { to: "/work", label: t("Work") }} eyebrow={def ? t(def.label) : job.type} title={job.title}
        subtitle={[job.group?.label, job.place?.label, job.due_at ? t("due {{date}}", { date: formatDate(job.due_at) }) : null].filter(Boolean).join(" · ")}
        actions={<Menu label={t("Job options")} items={[
          job.status === "active" ? { label: t("Pause"), icon: "clock", onSelect: () => void setStatus("paused") } : { label: t("Resume"), icon: "play", onSelect: () => void setStatus("active") },
          { label: t("Mark finished"), icon: "done", onSelect: () => void setStatus("done") },
          { label: t("Cancel job"), icon: "delete", danger: true, onSelect: () => void setStatus("cancelled") },
        ]} />}>
        {job.progress.total > 0 && <Progress done={job.progress.done} total={job.progress.total} exceptions={job.progress.exceptions} label={job.title} />}
        {job.status !== "active" && <Chip tone="neutral">{t(job.status === "done" ? "Finished" : job.status === "paused" ? "Paused" : job.status === "cancelled" ? "Cancelled" : "Planned")}</Chip>}
      </PageHeader>
      {Custom ? <Custom job={job} items={items} refresh={() => void q.refetch()} /> : (
        <>
          <Tabs value={tab} onChange={setTab} tabs={[{ id: "work", label: t("Worklist"), count: items.filter((i) => i.status === "pending").length }, { id: "results", label: t("Records") }, { id: "tasks", label: t("Tasks"), count: tasks.filter((x) => x.status === "open").length }]} />
          {tab === "work" && <Worklist job={job} items={items} refresh={() => void q.refetch()} />}
          {tab === "results" && <Timeline job={job.id} filters={false} />}
          {tab === "tasks" && <List>{tasks.map((x) => <TaskRow key={x.id} task={x} today={today} />)}</List>}
        </>
      )}
    </Page>
  );
}

/** The default job body: go down the list, record the job's event for each animal, mark exceptions. */
export function Worklist({ job, items, refresh }: { job: JobDetail; items: JobItemDTO[]; refresh(): void }) {
  const def = registry.jobs.get(job.type);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const openComposer = useOpenComposer();
  // Animals opened from the worklist come back here, not to the herd list.
  const from = useFromHere(job.title);
  const shown = items.filter((i) => filter === "all" || i.status === "pending" || i.status === "exception");
  const mark = async (item: JobItemDTO, status: JobItemDTO["status"]) => { await api.patch(`/jobs/${job.id}/items/${item.id}`, { status }); refresh(); };
  const record = (item: JobItemDTO) => {
    if (!def?.itemEvent) return;
    const evDef = registry.event(def.itemEvent);
    const role = evDef?.subjects.find((s) => s.kind === item.subject_kind)?.role ?? "animal";
    openRecord({ initial: { type: def.itemEvent, subjects: [{ kind: item.subject_kind, id: item.subject_id, role, label: item.label }], job_id: job.id }, onDone: async (e) => { await api.patch(`/jobs/${job.id}/items/${item.id}`, { status: "done", event_id: e.id }); refresh(); } });
  };
  if (!items.length) return <Empty icon="list" title={t("No animals in this job yet")} body={t("Add a group or tell Hondo which cattle are in it.")} action={<Button icon="mic" onClick={() => openComposer({ draft: t("Add to {{job}}: ", { job: job.title }) })}>{t("Tell Hondo")}</Button>} />;
  return (
    <>
      <div className="list-summary">
        <div className="filter-chips">
          <button type="button" className={cx("choice", filter === "pending" && "choice-on")} aria-pressed={filter === "pending"} onClick={() => setFilter("pending")}>{t("To do")}</button>
          <button type="button" className={cx("choice", filter === "all" && "choice-on")} aria-pressed={filter === "all"} onClick={() => setFilter("all")}>{t("All")}</button>
        </div>
      </div>
      <ul className="worklist">
        {shown.map((i) => (
          <li key={i.id} className={cx("work-item", `is-${i.status}`)}>
            {i.subject_kind === "animal" ? <Link to={`/cattle/${i.subject_id}`} state={from} className="work-subject">{i.label}</Link> : <span className="work-subject">{i.label}</span>}
            <span className="work-status">{i.status === "done" ? <Chip tone="good" icon="done">{t("Done")}</Chip> : i.status === "exception" ? <Chip tone="warn" icon="warn">{t("Needs a look")}</Chip> : i.status === "skipped" ? <Chip tone="neutral">{t("Skipped")}</Chip> : null}</span>
            <span className="work-actions">
              {def?.itemEvent && i.status !== "done" && <Button size="sm" variant="primary" onClick={() => record(i)}>{t("Record")}</Button>}
              {i.status === "pending" && <Menu label={t("More")} items={[{ label: t("Mark done"), icon: "check", onSelect: () => void mark(i, "done") }, { label: t("Skip"), icon: "minus", onSelect: () => void mark(i, "skipped") }, { label: t("Needs a look"), icon: "warn", onSelect: () => void mark(i, "exception") }]} />}
              {i.status !== "pending" && <Button size="sm" variant="ghost" onClick={() => void mark(i, "pending")}>{t("Reopen")}</Button>}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function WorkspaceRoute() {
  const { workspace } = useParams();
  const def = registry.workspaces.find((w) => w.id === workspace);
  const Component = workspace ? workspaceComponent(workspace) : undefined;
  if (!def) return <NotFound title={t("We couldn't find that workspace")} />;
  if (!Component) return <GenericWorkspace id={def.id} />;
  return <Component />;
}

/** Until a module provides its own workspace, list its jobs and offer to start one. */
function GenericWorkspace({ id }: { id: string }) {
  const def = registry.workspaces.find((w) => w.id === id)!;
  const jobs = useApi<{ items: JobSummary[] }>(`/jobs?workspace=${id}&status=all`);
  return (
    <Page>
      <PageHeader back={{ to: "/work", label: t("Work") }} title={t(def.label)} subtitle={t(def.description)} actions={<PinButton workspace={def.id} />} />
      <Section title={t("Jobs")}>{jobs.isLoading ? <Loading /> : jobs.data?.items.length ? <List>{jobs.data.items.map((j) => <Row key={j.id} icon={def.icon} to={`/work/jobs/${j.id}`} title={j.title} meta={t(j.status === "done" ? "Finished" : j.status === "paused" ? "Paused" : j.status === "cancelled" ? "Cancelled" : j.status === "planned" ? "Planned" : "In progress")} />)}</List> : <p className="muted teach">{t("No jobs here yet. Start one from Work, or tell Hondo what you want to do.")}</p>}</Section>
    </Page>
  );
}

export { ROLE_LABELS, useCrew };
